import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { ThemeConfig } from '@/types/theme';

interface ThemeContextValue {
  config: ThemeConfig;
  setConfig: (config: ThemeConfig) => void;
  mode: 'light' | 'dark';
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children, initialConfig }: { children: ReactNode; initialConfig?: ThemeConfig }) {
  const [config, setConfig] = useState<ThemeConfig>(initialConfig || {
    company_name: 'Acme Phone Repairs',
    logo_url: null,
    primary_colour: '#1e40af',
    accent_colour: '#f59e0b',
    phone: null,
    email: null,
    currency_symbol: '£',
    checkout_mode: 'enquiry_only',
    gateways_available: [],
    show_prices: false,
    shipping: { enabled: false, rate_pence: 0, label: 'Standard Delivery' },
  });

  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dj-theme-mode');
      return saved === 'light' ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    document.documentElement.style.setProperty('--sf-primary', config.primary_colour);
    document.documentElement.style.setProperty('--sf-accent', config.accent_colour);
  }, [config]);

  useEffect(() => {
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('dj-theme-mode', mode);
  }, [mode]);

  const toggleMode = () => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ config, setConfig, mode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
